import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import QuizArena from "./pages/QuizArena";
import BudgetSimulator from "./pages/BudgetSimulator";
import AIMentor from "./pages/AIMentor";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/AdminDashboard";
import Courses from "./pages/Courses";
import Tools from "./pages/Tools";
import Goals from "./pages/Goals";
import HealthScore from "./pages/HealthScore";
import BudgetTracker from "./pages/BudgetTracker";
import InvestmentCalculator from "./pages/InvestmentCalculator";
import LeadsInbox from "./pages/LeadsInbox";
import About from "./pages/About";
import DailyQuiz from "./pages/DailyQuiz";
import LegalPage from "./pages/LegalPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import NotFound from "./pages/NotFound";
import AddToHomeScreen from "./components/AddToHomeScreen";
import AnalyticsTracker from "./components/AnalyticsTracker";
import ConsentBanner from "./components/ConsentBanner";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#061530]">
        <div className="text-[#C0975C] text-2xl font-semibold animate-pulse" style={{ fontFamily: "'Outfit', sans-serif" }}>Precision Wealth</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#061530]">
        <div className="text-[#C0975C] text-2xl font-semibold animate-pulse" style={{ fontFamily: "'Outfit', sans-serif" }}>Precision Wealth</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.is_admin) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-[#061530]">
        <div className="noise-overlay" />
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/privacy" element={<LegalPage type="privacy" />} />
            <Route path="/terms" element={<LegalPage type="terms" />} />
            <Route path="/disclosures" element={<LegalPage type="disclosures" />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/quiz" element={<ProtectedRoute><QuizArena /></ProtectedRoute>} />
            <Route path="/quiz/:category" element={<ProtectedRoute><QuizArena /></ProtectedRoute>} />
            <Route path="/budget-sim" element={<ProtectedRoute><BudgetSimulator /></ProtectedRoute>} />
            <Route path="/business-sim" element={<Navigate to="/dashboard" replace />} />
            <Route path="/mentor" element={<ProtectedRoute><AIMentor /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/courses" element={<ProtectedRoute><Courses /></ProtectedRoute>} />
            <Route path="/tools" element={<ProtectedRoute><Tools /></ProtectedRoute>} />
            <Route path="/goals" element={<ProtectedRoute><Goals /></ProtectedRoute>} />
            <Route path="/health" element={<ProtectedRoute><HealthScore /></ProtectedRoute>} />
            <Route path="/budget-tracker" element={<ProtectedRoute><BudgetTracker /></ProtectedRoute>} />
            <Route path="/investment-calculator" element={<ProtectedRoute><InvestmentCalculator /></ProtectedRoute>} />
            {/* Retired gamification pages kept as redirects to Dashboard */}
            <Route path="/leaderboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="/rewards" element={<Navigate to="/dashboard" replace />} />
            <Route path="/badges" element={<Navigate to="/dashboard" replace />} />
            <Route path="/products" element={<Navigate to="/dashboard" replace />} />
            <Route path="/referrals" element={<Navigate to="/dashboard" replace />} />
            {/* Retired youth/community pages */}
            <Route path="/community-tools" element={<Navigate to="/dashboard" replace />} />
            <Route path="/cv-builder" element={<Navigate to="/dashboard" replace />} />
            <Route path="/interview-simulator" element={<Navigate to="/dashboard" replace />} />
            <Route path="/about" element={<ProtectedRoute><About /></ProtectedRoute>} />
            <Route path="/daily-quiz" element={<ProtectedRoute><DailyQuiz /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<AdminRoute><LeadsInbox /></AdminRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ConsentBanner />
        </BrowserRouter>
        <AddToHomeScreen />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#0A0A0A',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#fff',
            },
          }}
        />
      </div>
    </AuthProvider>
  );
}

export default App;
